import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ModalAddToRAComponent } from './modal-add-to-ra/modal-add-to-ra.component';
import { RadiationAreaItem } from './radiation-area-item';
import { RadiationAreaService } from './radiation-area-service.service';
import { Observable, Subscription } from 'rxjs';
import FileV2 from '../../../services/file/file-v2.model';
import { FileV2Service } from 'src/app/services/file/file-v2.service';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { RadiationAreaStateModel } from './store/radiation-area.state';

@Component({
  selector: 'app-radiation-area',
  templateUrl: './radiation-area.component.html',
  styleUrls: ['./radiation-area.component.scss']
})
export class RadiationAreaComponent extends BaseComponent implements OnInit, OnDestroy {

  public radAreas: RadiationAreaItem[] = [];
  public radiationAreaItems$!: Observable<RadiationAreaStateModel>;
  public radiationAreaItemSubscription: Subscription = new Subscription;

  public currentUserSubscription: Subscription = new Subscription;

  public nowdate: Date = new Date();
  loading = false;
  fileServer!: FileV2 | null;

  public maxHeight = '0px';

  constructor(
    public radiationAreaService: RadiationAreaService,
    private file2Service: FileV2Service,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getRadiationAreaItems();
    this.getCurrentUser();
  }

  ngAfterViewInit(): void {
    this.responsiveFunction();
  }

  override ngOnDestroy(): void {
    this.radiationAreaItemSubscription?.unsubscribe();
  }

  getRadiationAreaItems() {
    this.loading = true;
    this.radiationAreaItems$ = this.store.select(state => state.RadiationArea);
    this.radiationAreaItemSubscription = this.radiationAreaItems$.subscribe(state => {
      this.loading = state.loading;
      const radiationAreaItems = state.radiationAreaItems;
      if (radiationAreaItems?.length) {
        this.radAreas = radiationAreaItems?.filter(ra => ra.status === 1).sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      }
    });
  }

  addRA() {
    this.dialog.open(ModalAddToRAComponent, {
      maxWidth: '500px'
    });
  }

  editRA(item: RadiationAreaItem) {
    this.radiationAreaService.selectedItem = item;
    this.dialog.open(ModalAddToRAComponent, {
      maxWidth: '500px'
    });
  }

  deleteRA(item: RadiationAreaItem) {
    const self = this;
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('Rad_Area_Delete').description,
        icon: 'warn'
      }
    });
    confirm.afterClosed().toPromise().then(async data => {
      if (data && item.id) {
        this.radiationAreaService.deleteRAI(item.id).toPromise().then(() => {
          // Remove the item from this.radAreas
          this.radAreas = this.radAreas.filter(ra => ra.id !== item.id);
        });
      }
    });
  }

  /**
   * Uses edit metadata when present and falls back to creation metadata so every card has an audit timestamp.
   * @param radArea The radiation area card being displayed.
   */
  getLastChangedOn(radArea: RadiationAreaItem): Date {
    // Show the most recent persisted change date under each radiation area card.
    return radArea.updatedOn ?? radArea.createdOn;
  }

  /**
   * Resolves the display name for the user who made the latest radiation area change.
   * @param radArea The radiation area card being displayed.
   */
  getLastChangedByName(radArea: RadiationAreaItem): string {
    // Prefer API-resolved names, but support real-time updates that may only include user IDs.
    if (radArea.updatedOn) {
      return radArea.updatedByUser || this.users.find(user => user.id == radArea.updatedBy)?.name || '';
    }
    return radArea.createdByUser || this.users.find(user => user.id == radArea.createdBy)?.name || '';
  }

  /**
   * Resolves initials for the user who made the latest radiation area change.
   * @param radArea The radiation area card being displayed.
   */
  getLastChangedByInitials(radArea: RadiationAreaItem): string {
    // Prefer catalog initials when an ID is available, otherwise derive initials from the resolved name.
    const userID = radArea.updatedOn ? radArea.updatedBy : radArea.createdBy;
    const initials = this.users.find(user => user.id == userID)?.initials;
    if (initials) return initials;

    return this.getInitialsFromName(this.getLastChangedByName(radArea));
  }

  /**
   * Builds initials from a full name when the user catalog cannot resolve a stored user ID.
   * @param name The display name returned by the radiation area API.
   */
  private getInitialsFromName(name: string): string {
    // Fallback for list responses that provide names but not user IDs.
    return name
      .split(' ')
      .filter(part => part.length)
      .slice(0, 2)
      .map(part => part[0].toUpperCase())
      .join('');
  }

  responsiveFunction() {
    this.innerWidth = window.innerWidth;
    this.innerHeight = window.innerHeight;
    this.maxHeight = (parseInt((this.innerHeight * 0.50).toFixed(0), 10) - 100) + 'px';
  }

}
